/**
 * Profile Service
 * Handles user profile and yanamas personality configuration
 */

export interface UserProfile {
  id: number
  username: string
  email: string
  first_name?: string
  last_name?: string
  horton_emotional_approach: string
  horton_conversation_style: string
  personality_updated_at: string
  personality_change_count: number
}

export interface PersonalityOption {
  name: string
  description: string
  characteristics: string[]
  example_approach: string
  best_for?: string[]
}

export interface PersonalityOptions {
  emotional_approaches: Record<string, PersonalityOption>
  conversation_styles: Record<string, PersonalityOption>
}

export interface Persona {
  persona_name: string
  emoji: string
  tagline: string
  emotional_approach: string
  conversation_style: string
  best_for: string[]
  characteristics: string[]
}

export interface PersonasResponse {
  personas: Record<string, Persona>
}

export interface PersonalityStats {
  total_changes: number
  current_emotional_approach: string
  current_conversation_style: string
  last_updated: string
  popular_combinations: Array<{
    emotional_approach: string
    conversation_style: string
    usage_count: number
  }>
}

export interface PersonalityRecommendation {
  emotional_approach: string
  conversation_style: string
  reason: string
  confidence: number
}

class ProfileService {
  private baseUrl = '/profile'

  /**
   * Get current user profile
   */
  async getUserProfile(): Promise<UserProfile> {
    const response = await fetch(`${this.baseUrl}/api/user-profile`, {
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error('Failed to fetch user profile')
    }

    const contentType = response.headers.get('content-type')
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error('Not authenticated')
    }

    const data = await response.json()
    return data.profile
  }

  /**
   * Get personality configuration options (DEPRECATED - use getPersonas)
   */
  async getPersonalityOptions(): Promise<PersonalityOptions> {
    const response = await fetch(`${this.baseUrl}/api/horton-personality-options`, {
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error('Failed to fetch personality options')
    }

    const data = await response.json()
    return data.options || { emotional_approaches: {}, conversation_styles: {} }
  }

  /**
   * Get all personality personas (9 personas with faces and names)
   */
  async getPersonas(): Promise<Record<string, Persona>> {
    const response = await fetch(`${this.baseUrl}/api/horton-personality-options`, {
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error('Failed to fetch personality personas')
    }

    const data = await response.json()
    return data.personas || {}
  }

  /**
   * Update yanamas personality settings
   */
  async updateHortonPersonality(
    emotional_approach: string,
    conversation_style: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${this.baseUrl}/api/update-horton-personality`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({
        emotional_approach,
        conversation_style
      })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Failed to update personality')
    }

    return await response.json()
  }

  /**
   * Get personality usage statistics
   */
  async getPersonalityStats(): Promise<PersonalityStats> {
    const response = await fetch(`${this.baseUrl}/api/horton-personality-stats`, {
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error('Failed to fetch personality stats')
    }

    const data = await response.json()
    return data.stats
  }

  /**
   * Get AI-powered personality recommendations
   */
  async getPersonalityRecommendations(): Promise<PersonalityRecommendation[]> {
    const response = await fetch(`${this.baseUrl}/api/recommend-horton-personality`, {
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error('Failed to fetch personality recommendations')
    }

    const data = await response.json()
    return data.recommendations
  }
}

export const profileService = new ProfileService()

